import React, { useMemo, useState } from "react"

import { TagDotsPreview } from "@/tags"
import {
  Badge,
  CopyableId,
  TruncatedText,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"

import { EntityPickerDialog } from "@/components/entity-picker/EntityPickerDialog"
import type { Persona } from "@/types/api/Persona"
import { getAvatarColor, getAvatarUrl } from "@/utils/avatarIdentity"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

import { getPersonaSortValue } from "../utils/personaSortValue"
import { isBuiltInPersona, resolvePersonaLanguage } from "./persona-utils"

// Trimmed from the personas list page's set to what a picker needs — the prompt
// stays, since it is what distinguishes two personas at a glance.
const NAME_COLUMN_WIDTH = 240
const NAME_COLUMN_MIN_WIDTH = 200
const PROMPT_COLUMN_WIDTH = 240
const PROMPT_MIN_WIDTH = 160
const LANGUAGE_COLUMN_WIDTH = 110
const DATE_COLUMN_WIDTH = 100
const TAGS_COLUMN_WIDTH = 110

const DEFAULT_SORT: SortDescriptor = {
  columnId: "display_name",
  direction: "asc",
}

// Column model kept at module scope (deps passed in) so the JSX cells don't
// trip react/no-unstable-nested-components; the component memoizes the result.
function buildPersonaPickerColumns({
  tagColorMap,
}: {
  tagColorMap?: Record<string, string | null | undefined>
}): ColumnDef<Persona>[] {
  return [
    {
      id: "display_name",
      header: "Persona Name",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      cell: (persona) => (
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-end justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: getAvatarColor(persona.id) }}
          >
            <img
              src={persona.avatar_url || getAvatarUrl(persona.id)}
              className="h-full w-full rounded-full"
              alt=""
            />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <TruncatedText className="font-semibold">
              {persona.display_name}
            </TruncatedText>
            {isBuiltInPersona(persona) && (
              <Badge variant="outline" className="shrink-0 text-xs">
                Built-in
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "simulated_user_prompt",
      header: "Prompt",
      width: PROMPT_COLUMN_WIDTH,
      minWidth: PROMPT_MIN_WIDTH,
      sortable: true,
      cell: (persona) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {persona.simulated_user_prompt || "No prompt"}
        </span>
      ),
    },
    {
      id: "language",
      header: "Language",
      width: LANGUAGE_COLUMN_WIDTH,
      sortable: true,
      cell: (persona) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {resolvePersonaLanguage(persona)}
        </span>
      ),
    },
    {
      id: "last_updated_at",
      header: "Updated",
      width: DATE_COLUMN_WIDTH,
      sortable: true,
      cell: (persona) => (
        <span
          className="whitespace-nowrap text-coval-text-muted"
          title={formatFullTimestamp(persona.attribution?.updated_at)}
        >
          {formatRelativeTimestamp(persona.attribution?.updated_at)}
        </span>
      ),
    },
    {
      id: "tags",
      header: "Tags",
      width: TAGS_COLUMN_WIDTH,
      align: "right",
      flex: false,
      cell: (persona) => (
        <div className="flex min-w-0 justify-end">
          <TagDotsPreview tags={persona.tags ?? []} tagColorMap={tagColorMap} />
        </div>
      ),
    },
    {
      id: "id",
      header: "ID",
      variant: "id",
      pinned: "right",
      cell: (persona) => <CopyableId id={persona.id} label="Copy Persona ID" />,
    },
  ]
}

interface PersonaPickerDialogProps {
  isOpen: boolean
  onCancel: () => void
  onApply: () => void
  personas: Persona[]
  isLoading?: boolean
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  allowMultiple?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  catalogPanel?: React.ReactNode
  createAction?: React.ReactNode
}

// `tag:<value>` narrows the search to tags only, matching the syntax the
// previous persona dropdown supported.
const TAG_QUERY_PREFIX = /^tag:(.*)$/i

const matchesPersonaTag = (persona: Persona, tagQuery: string) =>
  (persona.tags ?? []).some((tag) => tag.toLowerCase().includes(tagQuery))

/** Matches one persona against an already trimmed, lowercased query. */
export function personaMatchesSearch(persona: Persona, query: string) {
  const tagFilterMatch = query.match(TAG_QUERY_PREFIX)
  if (tagFilterMatch) {
    const tagQuery = tagFilterMatch[1].trim()
    // A bare `tag:` shows everything until a tag value is typed.
    return tagQuery === "" || matchesPersonaTag(persona, tagQuery)
  }
  return [
    persona.display_name,
    persona.id,
    persona.simulated_user_prompt,
    resolvePersonaLanguage(persona),
    ...(persona.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(query)
}

export const PersonaPickerDialog: React.FC<PersonaPickerDialogProps> = ({
  isOpen,
  onCancel,
  onApply,
  personas,
  isLoading = false,
  selectedIds,
  onSelectionChange,
  allowMultiple = true,
  tagColorMap,
  createAction,
  catalogPanel,
}) => {
  const [sort, setSort] = useState<SortDescriptor | null>(DEFAULT_SORT)
  const sortedPersonas = useMemo(() => {
    const activeSort = sort ?? DEFAULT_SORT
    return personas.toSorted((a, b) => {
      const first = getPersonaSortValue(a, activeSort.columnId)
      const second = getPersonaSortValue(b, activeSort.columnId)
      const comparison = first.localeCompare(second)
      return activeSort.direction === "desc" ? -comparison : comparison
    })
  }, [personas, sort])
  const columns = useMemo(
    () => buildPersonaPickerColumns({ tagColorMap }),
    [tagColorMap],
  )

  return (
    <EntityPickerDialog<Persona>
      isOpen={isOpen}
      onCancel={onCancel}
      onApply={onApply}
      title="Select Personas"
      items={sortedPersonas}
      columns={columns}
      getItemId={(persona) => persona.id}
      getItemLabel={(persona) => persona.display_name || persona.id}
      matchesSearch={personaMatchesSearch}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      isLoading={isLoading}
      allowMultiple={allowMultiple}
      defaultSort={sort ?? DEFAULT_SORT}
      onSortChange={setSort}
      rowAttributes={(persona) => ({
        "data-testid": `persona-option-${persona.id}`,
        "data-persona-name": persona.display_name || undefined,
      })}
      searchPlaceholder="Search personas by name, prompt, language, or tag:name..."
      createAction={createAction}
      catalogPanel={catalogPanel}
      emptyState="No personas yet"
      searchEmptyState="No personas match your search"
      data-testid="persona-picker-dialog"
    />
  )
}
